import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../Components/Redux/store';
import { setAuth } from '../Components/Redux/authSlice';
import { Link } from 'react-router-dom';
import { FaEye, FaTrash, FaSearch, FaPlus, FaFileExport, FaMoneyBillWave, FaChartLine } from 'react-icons/fa';
import './fees.css';

interface Fee {
  id: number;
  student: string | number;
  student_name: string;
  month: string;
  total_amount: string;
  amount_paid: string;
  date_paid: string;
  class_name: string;
  balance: string;
  receipt_number: number;
  collected_by: string;
}

// Helper function to parse currency strings from backend
const parseCurrency = (value: string | number): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  // Remove "Ksh", "KSh", whitespace, and commas, then parse
  const cleaned = value.toString().replace(/Ksh\s*/gi, '').replace(/,/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const ShowFees: React.FC = () => {
  const [fees, setFees] = useState<Fee[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [userRole, setUserRole] = useState<string | null>(null);
  const [monthFilter, setMonthFilter] = useState<string>('');
  const [stats, setStats] = useState({
    totalCollected: 0,
    totalBalance: 0,
    totalRecords: 0,
    avgPayment: 0
  });
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth.value);

  const getCookie = (name: string) => {
    const cookieName = name + "=";
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArray = decodedCookie.split(';');
    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1);
      }
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length, cookie.length);
      }
    }
    return '';
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const fetchData = async () => {
    try {
      const token = getCookie('token');
      if (!token) {
        dispatch(setAuth(false));
        return;
      }
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const userResponse = await axios.get('/users/user', {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setUserRole(userResponse.data.user.role);
      dispatch(setAuth(true));
    } catch (e) {
      dispatch(setAuth(false));
    }
  };

  const fetchFees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/fees/fees/?page=${page}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const results = response.data.results;
      setFees(results);
      setTotalPages(response.data.total_pages);

      // Calculate statistics using the currency parser
      const totalCollected = results.reduce((sum: number, fee: Fee) => {
        return sum + parseCurrency(fee.amount_paid);
      }, 0);

      const totalBalance = results.reduce((sum: number, fee: Fee) => {
        return sum + parseCurrency(fee.balance);
      }, 0);

      const avgPayment = results.length > 0 ? totalCollected / results.length : 0;

      setStats({
        totalCollected,
        totalBalance,
        totalRecords: results.length,
        avgPayment
      });
    } catch (error) {
      console.error('Error fetching fees:', error);
    }
    setLoading(false);
  };
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleDelete = async (id: number) => {
    if (window.confirm('Ma hubtaa inaad rabto inaad tirtirto diiwaankan lacagta ah?')) {
      try {
        await axios.delete(`/fees/fees/${id}/`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        setFees(fees.filter(fee => fee.id !== id));
      } catch (error) {
        console.error('Error deleting fee record:', error);
      }
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (auth) {
      fetchFees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, page]);

  const handleNextPage = () => {
    setPage(prevPage => prevPage + 1);
  };

  const handlePrevPage = () => {
    setPage(prevPage => prevPage - 1);
  };

  const filteredFees = fees.filter(fee => {
    const matchesName = fee.student_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMonth = monthFilter ? fee.month === monthFilter : true;
    return matchesName && matchesMonth;
  });

  const exportToCSV = () => {
    const headers = ['#', 'Name', 'Month', 'Total', 'Paid', 'Date', 'Class', 'Balance'];
    const csvData = filteredFees.map((fee, index) => [
      index + 1,
      fee.student_name,
      fee.month,
      fee.total_amount,
      fee.amount_paid,
      fee.date_paid,
      fee.class_name,
      fee.balance
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fees-report-${new Date().toISOString()}.csv`;
    a.click();
  };

  return (
    <div className="dashboard-content">
      {auth ? (
        <div className="fees-container">
          {/* Header Section */}
          <div className="fees-header">
            <div className="header-content">
              <div className="header-title">
                <FaMoneyBillWave className="header-icon" />
                <div>
                  <h2>Maamulka Lacagaha</h2>
                  <p className="header-subtitle">La soco oo maamul lacag bixinta ardayda</p>
                </div>
              </div>
              <div className="header-actions">
                <Link to="/add-fees" className="btn-primary-modern">
                  <FaPlus /> Diiwaangeli Lacag
                </Link>
                <button onClick={exportToCSV} className="btn-secondary-modern">
                  <FaFileExport /> Dhoofi CSV
                </button>
              </div>
            </div>
          </div>

          {/* Statistics Cards */}
          <div className="stats-grid">
            <div className="stat-card stat-card-blue">
              <div className="stat-icon-wrapper">
                <FaMoneyBillWave className="stat-icon" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Wadarta La Ururiyay</p>
                <h3 className="stat-value">KSh {stats.totalCollected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                <span className="stat-trend positive">Boggan</span>
              </div>
            </div>

            <div className="stat-card stat-card-green">
              <div className="stat-icon-wrapper">
                <FaChartLine className="stat-icon" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Celceliska Lacag Bixinta</p>
                <h3 className="stat-value">KSh {stats.avgPayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h3>
                <span className="stat-trend">Arday walba</span>
              </div>
            </div>

            <div className="stat-card stat-card-purple">
              <div className="stat-icon-wrapper">
                <FaFileExport className="stat-icon" />
              </div>
              <div className="stat-content">
                <p className="stat-label">Wadarta Diiwaanada</p>
                <h3 className="stat-value">{stats.totalRecords}</h3>
                <span className="stat-trend">Boggan</span>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="filters-section">
            <div className="filter-group">
              <div className="search-wrapper">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Ku raadi magaca ardayga..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <select
                className="month-filter"
                value={monthFilter}
                onChange={(e) => setMonthFilter(e.target.value)}
              >
                <option value="">Dhamaan Bilaha</option>
                {['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December']
                  .map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
              </select>
            </div>
          </div>

          {/* Table Section */}
          <div className="table-container">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Soo dejinaya diiwaanada lacagta...</p>
              </div>
            ) : (
              <>
                <div className="table-wrapper">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Magaca Ardayga</th>
                        <th>Bisha</th>
                        <th>Wadarta Guud</th>
                        <th>Lacagta La Bixiyay</th>
                        <th>Taariikhda</th>
                        <th>Fasalka</th>
                        <th>Haraaga</th>
                        <th>Falalka</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredFees.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="empty-state">
                            <FaSearch />
                            <p>Diiwaano lacag ah lama helin</p>
                          </td>
                        </tr>
                      ) : (
                        filteredFees.map((fee, index) => (
                          <tr key={fee.id} className="table-row">
                            <td>{(page - 1) * 15 + index + 1}</td>
                            <td className="student-name">{fee.student_name}</td>
                            <td>{fee.month}</td>
                            <td className="amount">{fee.total_amount}</td>
                            <td className="amount paid">{fee.amount_paid}</td>
                            <td>{new Date(fee.date_paid).toLocaleDateString()}</td>
                            <td>
                              <span className="class-badge">{fee.class_name}</span>
                            </td>
                            <td>
                              <span className={`balance-badge ${parseCurrency(fee.balance) > 0 ? 'pending' : 'paid'}`}>
                                {fee.balance}
                              </span>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link
                                  className="btn-action btn-view"
                                  to={`/${fee.id}/`}
                                  title="Arag Faahfaahinta"
                                >
                                  <FaEye />
                                </Link>
                                {userRole !== 'accountant' && (
                                  <button
                                    className="btn-action btn-delete"
                                    onClick={() => handleDelete(fee.id)}
                                    title="Tirtir Diiwaanka"
                                  >
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="pagination">
                  <button
                    className="pagination-btn"
                    disabled={page === 1}
                    onClick={handlePrevPage}
                  >
                    Hore
                  </button>
                  <span className="pagination-info">
                    Bogga {page} ee {totalPages}
                  </span>
                  <button
                    className="pagination-btn"
                    disabled={page >= totalPages}
                    onClick={handleNextPage}
                  >
                    Xiga
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="auth-error">
          <h4>Ma aadan xaqiijin</h4>
          <p>Fadlan soo gal si aad u hesho boggan</p>
        </div>
      )}
    </div>
  );
};

export default ShowFees;